import React, { useState, useCallback, useMemo } from 'react';
import {
  colors,
  sectionTitleStyle,
  cardStyle,
  subheadingStyle,
  buttonPrimary,
  buttonSecondary,
  buttonGold,
  buttonDisabled,
  gridTwo,
  gridThree,
} from './styles/theme';
import {
  stateOptions,
  legalEntityOptions,
  businessOperationsCheckOptions,
  priorCarrierOptions,
  hartfordPolicyOptions,
  claimStatusOptions,
  priorPolicyTypeOptions,
  bodyTypeOptions,
  vehicleClassOptions,
  hitchTypeOptions,
  drivingRadiusOptions,
  vehicleUsageOptions,
  vehicleRegisteredToOptions,
  autoDeductibleOptions,
  sexOptions,
  maritalStatusOptions,
  licenseTypeOptions,
  liabilityLimitOptions,
  medPayOptions,
  umLimitOptions,
  coveredAutoSymbolOptions,
  rentalReimbursementLimitOptions,
  cargoLimitOptions,
  filingTypeOptions,
  plowLocationOptions,
  hazmatControlOptions,
} from './constants/formOptions';
import {
  Logo,
  ProgressBar,
  StepIndicator,
  ReviewItem,
  FormWrapper,
  SuccessPage,
} from './components/shared/FormLayout';
import {
  Input,
  Select,
  TextArea,
  RadioGroup,
  CheckboxGroup,
  PhoneInput,
  ZipInput,
  CurrencyInput,
  YearInput,
} from './components/FormInputs';
import { sanitizeString, generateId } from './utils/validation';
import { submitCommercialAutoForm } from './utils/commercialAutoApi';

// Step labels (8 steps)
const STEPS = [
  'Company Info',
  'Policy & History',
  'Vehicles',
  'Drivers',
  'Coverage',
  'Underwriting',
  'Additional Interests',
  'Review',
];

// Create empty object functions
function createEmptyVehicle() {
  return {
    id: generateId(),
    hasVin: '',
    vin: '',
    year: '',
    make: '',
    model: '',
    bodyType: '',
    vehicleClass: '',
    gvw: '',
    hitchType: '',
    drivingRadius: '',
    vehicleUsage: '',
    vehicleUseDescription: '',
    garagingZip: '',
    garagingCity: '',
    garagingState: '',
    registrationState: '',
    vehicleRegisteredTo: '',
    individuallyOwned: '',
    isLeased: '',
    isFinanced: '',
    costNew: '',
    currentValue: '',
    customizationValue: '',
    additionalAttachedEquipment: '',
    hasPhysicalDamage: '',
    hasCompCoverage: false,
    compDeductible: '',
    hasFullGlass: false,
    hasCollisionCoverage: false,
    collisionDeductible: '',
    hasTowing: false,
    hasRentalReimbursement: false,
  };
}

function createEmptyDriver() {
  return {
    id: generateId(),
    firstName: '',
    lastName: '',
    sex: '',
    dateOfBirth: '',
    maritalStatus: '',
    licenseType: '',
    licenseState: '',
    licenseNumber: '',
    hasCDL: '',
    cdlDateReceived: '',
    excludeDriver: false,
    requiresSR22: '',
  };
}

function createEmptyClaim() {
  return {
    id: generateId(),
    dateOfLoss: '',
    payoutAmount: '',
    description: '',
    driverOfLoss: '',
    claimStatus: '',
    atFault: '',
  };
}

function createEmptyLossPayee() {
  return {
    id: generateId(),
    payeeName: '',
    payeeStreet: '',
    payeeCity: '',
    payeeState: '',
    payeeZip: '',
    isAdditionalInterest: '',
  };
}

function createEmptyAdditionalInsured() {
  return {
    id: generateId(),
    insuredName: '',
    insuredStreet: '',
    insuredCity: '',
    insuredState: '',
    insuredZip: '',
    waiverOfSubrogation: '',
  };
}

export default function CommercialAutoForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [vehicleErrors, setVehicleErrors] = useState([]);
  const [driverErrors, setDriverErrors] = useState([]);
  const [claimErrors, setClaimErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Arrays for loops
  const [vehicles, setVehicles] = useState(() => [createEmptyVehicle()]);
  const [drivers, setDrivers] = useState(() => [createEmptyDriver()]);
  const [claims, setClaims] = useState([]);
  const [lossPayees, setLossPayees] = useState([]);
  const [additionalInsureds, setAdditionalInsureds] = useState([]);

  // Main form data
  const [formData, setFormData] = useState({
    // Step 1: Company Info
    legalEntity: '',
    legalBusinessName: '',
    hasDba: '',
    dbaName: '',
    fein: '',
    yearEstablished: '',
    contactFirstName: '',
    contactLastName: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    descriptionOfOperations: '',
    hasExistingLiabilityPolicy: '',
    allDriversValidLicense: '',
    anyDriversSR22: '',
    businessOperationsCheck: [],
    // Business Address
    businessStreet: '',
    businessStreet2: '',
    businessCity: '',
    businessState: '',
    businessZip: '',
    // Mailing Address
    mailingDifferent: '',
    mailingStreet: '',
    mailingCity: '',
    mailingState: '',
    mailingZip: '',

    // Step 2: Policy & History
    effectiveDate: '',
    expirationDate: '',
    autoLossesLast3Years: '',
    existingHartfordPolicies: [],
    hasPriorInsurance: '',
    priorCarrier: '',
    priorPolicyNumber: '',
    priorBILimits: '',
    priorPremium: '',
    priorEffectiveDate: '',
    priorExpirationDate: '',
    priorPolicyType: '',
    hasClaimHistory: '',

    // Step 5: Coverage
    liabilityLimit: '',
    medicalPayments: '',
    umbiLimit: '',
    hasUMPD: '',
    uimLimit: '',
    coveredAutoSymbols: 'Symbol 7 - Specifically Described Autos',
    hasAnyAuto: '',
    hasHiredAuto: false,
    annualRentalExpense: '',
    hasNonOwnedAuto: false,
    totalEmployees: '',
    hasDriveOtherCar: false,
    hasAdditionalDriverEndorsement: '',
    hasRentalReimbursementPolicy: '',
    rentalReimbursementLimit: '',
    hasCargoCoverage: '',
    cargoLimitDeductible: '',

    // Step 6: Underwriting & Filings
    dotNumber: '',
    requireStateFilings: '',
    stateFilingNumber: '',
    requireFederalFilings: '',
    filingTypes: [],
    // Underwriting questions
    allOwnedAutosIncluded: '',
    allOwnedAutosIncludedDetails: '',
    allVehiclesOwnedByApplicant: '',
    allVehiclesOwnedByApplicantDetails: '',
    obtainMVRVerifications: '',
    obtainMVRVerificationsDetails: '',
    allRegularDriversListed: '',
    allRegularDriversListedDetails: '',
    has50PercentInterest: '',
    has50PercentInterestDetails: '',
    otherBusinessOwnsVehicles: '',
    otherBusinessOwnsVehiclesDetails: '',
    rentHeavyVehicles: '',
    rentHeavyVehiclesDetails: '',
    performSnowPlowing: '',
    performSnowPlowingDetails: '',
    useHeavyPlowVehicles: '',
    numberOfPlowVehicles: '',
    plowLocations: [],
    transportHazmat: '',
    transportHazmatDetails: '',
    hazmatControls: [],
    transportGoodsOfOthers: '',
    transportGoodsOfOthersDetails: '',
    provideTowingServices: '',
    provideTowingServicesDetails: '',
    provideRentalVehicles: '',
    provideRentalVehiclesDetails: '',
    isSubsidiaryOrHasSubsidiaries: '',
    isSubsidiaryOrHasSubsidiariesDetails: '',
    vehiclesLeasedToOthers: '',
    vehiclesLeasedToOthersDetails: '',
    employeesUnder18Access: '',
    employeesUnder18AccessDetails: '',
    familyDriverExposures: '',
    familyDriverExposuresDetails: '',
    nonBusinessUseAllowed: '',
    nonBusinessUseAllowedDetails: '',
    employeesUseOwnAutos: '',
    employeesUseOwnAutosDetails: '',
    hasAutoSafetyProgram: '',
    hasAutoSafetyProgramDetails: '',
    regularMaintenanceSchedule: '',
    regularMaintenanceScheduleDetails: '',
    anyVehicleOver40kMiles: '',
    anyVehicleOver40kMilesDetails: '',
    policyDeclinedCancelledLast3Years: '',
    policyDeclinedCancelledLast3YearsDetails: '',
    bankruptcyLiensLast5Years: '',
    bankruptcyLiensLast5YearsDetails: '',
    crossStateLines: '',
    crossStateLinesDetails: '',

    // Step 7: Additional Interests
    hasLossPayee: '',
    hasAdditionalInsured: '',

    // Additional notes
    additionalNotes: '',
  });

  const totalSteps = STEPS.length;

  // Format date from YYYY-MM-DD to MM-DD-YYYY for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${month}-${day}-${year}`;
  };

  // Update functions
  const updateFormData = useCallback((field, value) => {
    const sanitizedValue = typeof value === 'string' ? sanitizeString(value) : value;
    setFormData((prev) => ({ ...prev, [field]: sanitizedValue }));
  }, []);

  const updateVehicle = useCallback((index, field, value) => {
    const sanitizedValue = typeof value === 'string' ? sanitizeString(value) : value;
    setVehicles((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: sanitizedValue };
      return updated;
    });
  }, []);

  const updateDriver = useCallback((index, field, value) => {
    const sanitizedValue = typeof value === 'string' ? sanitizeString(value) : value;
    setDrivers((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: sanitizedValue };
      return updated;
    });
  }, []);

  const updateClaim = useCallback((index, field, value) => {
    const sanitizedValue = typeof value === 'string' ? sanitizeString(value) : value;
    setClaims((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: sanitizedValue };
      return updated;
    });
  }, []);

  const updateLossPayee = useCallback((index, field, value) => {
    const sanitizedValue = typeof value === 'string' ? sanitizeString(value) : value;
    setLossPayees((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: sanitizedValue };
      return updated;
    });
  }, []);

  const updateAdditionalInsured = useCallback((index, field, value) => {
    const sanitizedValue = typeof value === 'string' ? sanitizeString(value) : value;
    setAdditionalInsureds((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: sanitizedValue };
      return updated;
    });
  }, []);

  // Add/remove functions
  const addVehicle = useCallback(() => setVehicles((prev) => [...prev, createEmptyVehicle()]), []);
  const removeVehicle = useCallback((index) => {
    setVehicles((prev) => prev.filter((_, i) => i !== index));
    setVehicleErrors((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const addDriver = useCallback(() => setDrivers((prev) => [...prev, createEmptyDriver()]), []);
  const removeDriver = useCallback((index) => {
    setDrivers((prev) => prev.filter((_, i) => i !== index));
    setDriverErrors((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const addClaim = useCallback(() => setClaims((prev) => [...prev, createEmptyClaim()]), []);
  const removeClaim = useCallback((index) => {
    setClaims((prev) => prev.filter((_, i) => i !== index));
    setClaimErrors((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const addLossPayee = useCallback(() => setLossPayees((prev) => [...prev, createEmptyLossPayee()]), []);
  const removeLossPayee = useCallback((index) => {
    setLossPayees((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const addAdditionalInsured = useCallback(() => setAdditionalInsureds((prev) => [...prev, createEmptyAdditionalInsured()]), []);
  const removeAdditionalInsured = useCallback((index) => {
    setAdditionalInsureds((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Toggle checkbox arrays
  const toggleCheckbox = useCallback((field, value) => {
    setFormData((prev) => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter((v) => v !== value) };
      }
      return { ...prev, [field]: [...current, value] };
    });
  }, []);

  // Calculate total vehicle value
  const totalVehicleValue = useMemo(
    () => vehicles.reduce((sum, v) => {
      const value = parseFloat((v.currentValue || v.costNew || '').replace(/[^0-9.]/g, '')) || 0;
      return sum + value;
    }, 0),
    [vehicles]
  );

  // Scroll to first error
  const scrollToFirstError = useCallback(() => {
    setTimeout(() => {
      const errorElements = document.querySelectorAll('[style*="rgb(197, 48, 48)"], [style*="#c53030"]');
      if (errorElements.length > 0) {
        errorElements[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = errorElements[0].querySelector('input, select, textarea');
        if (input) input.focus();
      }
    }, 100);
  }, []);

  // Validation
  const validateStep = useCallback((step) => {
    let stepErrors = {};
    let vehErrors = [];
    let drvErrors = [];
    let clmErrors = [];

    if (step === 1) {
      if (!formData.legalEntity) stepErrors.legalEntity = 'Required';
      if (!formData.legalBusinessName) stepErrors.legalBusinessName = 'Required';
      if (!formData.hasDba) stepErrors.hasDba = 'Required';
      if (formData.hasDba === 'Yes' && !formData.dbaName) stepErrors.dbaName = 'Required';
      if (!formData.yearEstablished) stepErrors.yearEstablished = 'Required';
      if (!formData.contactFirstName) stepErrors.contactFirstName = 'Required';
      if (!formData.contactLastName) stepErrors.contactLastName = 'Required';
      if (!formData.contactEmail) stepErrors.contactEmail = 'Required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) stepErrors.contactEmail = 'Invalid email';
      if (!formData.contactPhone) stepErrors.contactPhone = 'Required';
      if (!formData.descriptionOfOperations) stepErrors.descriptionOfOperations = 'Required';
      else if (formData.descriptionOfOperations.trim().split(/\s+/).length < 5) stepErrors.descriptionOfOperations = 'Please provide at least 5 words';
      if (!formData.businessStreet) stepErrors.businessStreet = 'Required';
      if (!formData.businessCity) stepErrors.businessCity = 'Required';
      if (!formData.businessState) stepErrors.businessState = 'Required';
      if (!formData.businessZip) stepErrors.businessZip = 'Required';
      if (!formData.mailingDifferent) stepErrors.mailingDifferent = 'Required';
      if (formData.mailingDifferent === 'Yes') {
        if (!formData.mailingStreet) stepErrors.mailingStreet = 'Required';
        if (!formData.mailingCity) stepErrors.mailingCity = 'Required';
        if (!formData.mailingState) stepErrors.mailingState = 'Required';
        if (!formData.mailingZip) stepErrors.mailingZip = 'Required';
      }
    }

    if (step === 2) {
      if (!formData.effectiveDate) stepErrors.effectiveDate = 'Required';
      if (!formData.hasPriorInsurance) stepErrors.hasPriorInsurance = 'Required';
      if (formData.hasPriorInsurance === 'Yes') {
        if (!formData.priorCarrier) stepErrors.priorCarrier = 'Required';
      }
      if (!formData.hasClaimHistory) stepErrors.hasClaimHistory = 'Required';
      if (formData.hasClaimHistory === 'Yes' && claims.length > 0) {
        clmErrors = claims.map((claim) => {
          const err = {};
          if (!claim.dateOfLoss) err.dateOfLoss = 'Required';
          if (!claim.description) err.description = 'Required';
          if (!claim.claimStatus) err.claimStatus = 'Required';
          return Object.keys(err).length > 0 ? err : null;
        });
      }
    }

    if (step === 3) {
      vehErrors = vehicles.map((vehicle) => {
        const err = {};
        if (!vehicle.vin?.trim()) {
          err.vin = 'VIN is required';
        } else if (vehicle.vin.trim().length !== 17) {
          err.vin = 'VIN must be 17 characters';
        }
        if (!vehicle.bodyType) err.bodyType = 'Required';
        if (!vehicle.vehicleClass) err.vehicleClass = 'Required';
        if (!vehicle.drivingRadius) err.drivingRadius = 'Required';
        if (!vehicle.vehicleUsage) err.vehicleUsage = 'Required';
        if (!vehicle.garagingZip) err.garagingZip = 'Required';
        if (!vehicle.garagingCity) err.garagingCity = 'Required';
        if (!vehicle.currentValue && !vehicle.costNew) err.currentValue = 'Required';
        return Object.keys(err).length > 0 ? err : null;
      });
    }

    if (step === 4) {
      drvErrors = drivers.map((driver) => {
        const err = {};
        if (!driver.firstName) err.firstName = 'Required';
        if (!driver.lastName) err.lastName = 'Required';
        if (!driver.dateOfBirth) err.dateOfBirth = 'Required';
        if (!driver.licenseNumber) err.licenseNumber = 'Required';
        if (!driver.licenseState) err.licenseState = 'Required';
        return Object.keys(err).length > 0 ? err : null;
      });
    }

    if (step === 5) {
      if (!formData.liabilityLimit) stepErrors.liabilityLimit = 'Required';
      if (!formData.medicalPayments) stepErrors.medicalPayments = 'Required';
      if (!formData.umbiLimit) stepErrors.umbiLimit = 'Required';
    }

    if (step === 6) {
      if (!formData.allOwnedAutosIncluded) stepErrors.allOwnedAutosIncluded = 'Required';
      if (!formData.obtainMVRVerifications) stepErrors.obtainMVRVerifications = 'Required';
      if (!formData.transportHazmat) stepErrors.transportHazmat = 'Required';
    }

    if (step === 7) {
      if (!formData.hasLossPayee) stepErrors.hasLossPayee = 'Required';
      if (!formData.hasAdditionalInsured) stepErrors.hasAdditionalInsured = 'Required';
    }

    return { stepErrors, vehErrors, drvErrors, clmErrors };
  }, [formData, vehicles, drivers, claims]);

  const validateCurrentStep = useCallback(() => {
    const { stepErrors, vehErrors, drvErrors, clmErrors } = validateStep(currentStep);

    setErrors(stepErrors);
    setVehicleErrors(vehErrors);
    setDriverErrors(drvErrors);
    setClaimErrors(clmErrors);

    const hasErrors =
      Object.keys(stepErrors).length > 0 ||
      vehErrors.some((e) => e && Object.keys(e).length > 0) ||
      drvErrors.some((e) => e && Object.keys(e).length > 0) ||
      clmErrors.some((e) => e && Object.keys(e).length > 0);

    if (hasErrors) {
      scrollToFirstError();
    }
    return !hasErrors;
  }, [currentStep, validateStep, scrollToFirstError]);

  // Navigation
  const nextStep = useCallback(() => {
    if (validateCurrentStep()) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [validateCurrentStep, totalSteps]);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const goToStep = useCallback((step) => {
    if (step < currentStep || validateCurrentStep()) {
      setCurrentStep(step);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep, validateCurrentStep]);

  // Submit
  const handleSubmit = useCallback(async () => {
    // Validate all steps
    let allErrors = {};
    let allVehErrors = [];
    let allDrvErrors = [];

    for (let s = 1; s <= 7; s++) {
      const { stepErrors, vehErrors, drvErrors } = validateStep(s);
      allErrors = { ...allErrors, ...stepErrors };
      if (s === 3) allVehErrors = vehErrors;
      if (s === 4) allDrvErrors = drvErrors;
    }

    if (
      Object.keys(allErrors).length > 0 ||
      allVehErrors.some((e) => e && Object.keys(e).length > 0) ||
      allDrvErrors.some((e) => e && Object.keys(e).length > 0)
    ) {
      setErrors(allErrors);
      setVehicleErrors(allVehErrors);
      setDriverErrors(allDrvErrors);
      setSubmitStatus('error');
      return;
    }

    // Clear any previous errors since validation passed
    setErrors({});
    setVehicleErrors([]);
    setDriverErrors([]);
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const result = await submitCommercialAutoForm({
        formData,
        vehicles,
        drivers,
        claims,
        lossPayees,
        additionalInsureds,
        totalVehicleValue,
      });

      if (result.success) {
        setSubmitStatus('success');
      } else {
        console.error('Submission failed:', result.error);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, vehicles, drivers, claims, lossPayees, additionalInsureds, totalVehicleValue, validateStep]);

  // Helper for underwriting questions with details
  const renderUnderwritingQuestion = (label, field, helpText = null) => (
    <div style={{ marginBottom: '20px' }}>
      <RadioGroup
        label={label}
        options={['Yes', 'No']}
        value={formData[field]}
        onChange={(val) => updateFormData(field, val)}
        error={errors[field]}
      />
      {helpText && (
        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: colors.mediumGray, marginTop: '4px' }}>
          {helpText}
        </p>
      )}
      {formData[field] === 'Yes' && (
        <div style={{ marginTop: '12px', marginLeft: '20px' }}>
          <TextArea
            label="Please provide additional details"
            value={formData[`${field}Details`]}
            onChange={(e) => updateFormData(`${field}Details`, e.target.value)}
            placeholder="Enter details..."
          />
        </div>
      )}
    </div>
  );

  // Render step content
  const renderStepContent = () => {
    // Step 1: Company Info
    if (currentStep === 1) {
      return (
        <div>
          <h2 style={sectionTitleStyle}>Company Information</h2>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Business Details</h3>
            <Select
              label="Legal Entity Type"
              required
              options={legalEntityOptions}
              value={formData.legalEntity}
              onChange={(e) => updateFormData('legalEntity', e.target.value)}
              error={errors.legalEntity}
            />
            <Input
              label="Legal Business Name"
              required
              placeholder="Your Company, LLC"
              value={formData.legalBusinessName}
              onChange={(e) => updateFormData('legalBusinessName', e.target.value)}
              error={errors.legalBusinessName}
            />
            <RadioGroup
              label="Does this business have a DBA (Doing Business As)?"
              required
              options={['Yes', 'No']}
              value={formData.hasDba}
              onChange={(val) => updateFormData('hasDba', val)}
              error={errors.hasDba}
            />
            {formData.hasDba === 'Yes' && (
              <Input
                label="DBA Name"
                required
                placeholder="DBA name"
                value={formData.dbaName}
                onChange={(e) => updateFormData('dbaName', e.target.value)}
                error={errors.dbaName}
              />
            )}
            <div style={gridTwo}>
              <Input
                label="FEIN (Tax ID)"
                required
                placeholder="XX-XXXXXXX"
                value={formData.fein}
                onChange={(e) => updateFormData('fein', e.target.value)}
                error={errors.fein}
              />
              <YearInput
                label="Year Business Established"
                required
                placeholder="2020"
                value={formData.yearEstablished}
                onChange={(e) => updateFormData('yearEstablished', e.target.value)}
                error={errors.yearEstablished}
              />
            </div>
            <TextArea
              label="Description of Operations"
              required
              placeholder="Describe your business operations in detail (minimum 5 words)"
              value={formData.descriptionOfOperations}
              onChange={(e) => updateFormData('descriptionOfOperations', e.target.value)}
              error={errors.descriptionOfOperations}
            />
            <Input
              label="Website"
              optional
              placeholder="www.yourcompany.com"
              value={formData.website}
              onChange={(e) => updateFormData('website', e.target.value)}
            />
          </div>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Contact Information</h3>
            <div style={gridTwo}>
              <Input
                label="First Name"
                required
                value={formData.contactFirstName}
                onChange={(e) => updateFormData('contactFirstName', e.target.value)}
                error={errors.contactFirstName}
              />
              <Input
                label="Last Name"
                required
                value={formData.contactLastName}
                onChange={(e) => updateFormData('contactLastName', e.target.value)}
                error={errors.contactLastName}
              />
            </div>
            <div style={gridTwo}>
              <Input
                label="Email"
                required
                type="email"
                placeholder="you@company.com"
                value={formData.contactEmail}
                onChange={(e) => updateFormData('contactEmail', e.target.value)}
                error={errors.contactEmail}
              />
              <PhoneInput
                label="Phone"
                required
                value={formData.contactPhone}
                onChange={(e) => updateFormData('contactPhone', e.target.value)}
                error={errors.contactPhone}
              />
            </div>
          </div>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Business Address</h3>
            <Input
              label="Street Address"
              required
              value={formData.businessStreet}
              onChange={(e) => updateFormData('businessStreet', e.target.value)}
              error={errors.businessStreet}
            />
            <Input
              label="Suite / Unit / Building"
              optional
              placeholder="Suite 100"
              value={formData.businessStreet2}
              onChange={(e) => updateFormData('businessStreet2', e.target.value)}
            />
            <div style={gridThree}>
              <Input
                label="City"
                required
                value={formData.businessCity}
                onChange={(e) => updateFormData('businessCity', e.target.value)}
                error={errors.businessCity}
              />
              <Select
                label="State"
                required
                options={stateOptions}
                value={formData.businessState}
                onChange={(e) => updateFormData('businessState', e.target.value)}
                error={errors.businessState}
              />
              <ZipInput
                label="ZIP Code"
                required
                value={formData.businessZip}
                onChange={(e) => updateFormData('businessZip', e.target.value)}
                error={errors.businessZip}
              />
            </div>
          </div>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Mailing Address</h3>
            <RadioGroup
              label="Is your mailing address different from your business address?"
              required
              options={['Yes', 'No']}
              value={formData.mailingDifferent}
              onChange={(val) => updateFormData('mailingDifferent', val)}
              error={errors.mailingDifferent}
            />
            {formData.mailingDifferent === 'Yes' && (
              <div style={{ marginTop: '16px', padding: '16px', backgroundColor: colors.offWhite, borderRadius: '8px' }}>
                <Input
                  label="Street Address"
                  required
                  value={formData.mailingStreet}
                  onChange={(e) => updateFormData('mailingStreet', e.target.value)}
                  error={errors.mailingStreet}
                />
                <div style={gridThree}>
                  <Input
                    label="City"
                    required
                    value={formData.mailingCity}
                    onChange={(e) => updateFormData('mailingCity', e.target.value)}
                    error={errors.mailingCity}
                  />
                  <Select
                    label="State"
                    required
                    options={stateOptions}
                    value={formData.mailingState}
                    onChange={(e) => updateFormData('mailingState', e.target.value)}
                    error={errors.mailingState}
                  />
                  <ZipInput
                    label="ZIP Code"
                    required
                    value={formData.mailingZip}
                    onChange={(e) => updateFormData('mailingZip', e.target.value)}
                    error={errors.mailingZip}
                  />
                </div>
              </div>
            )}
          </div>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Additional Business Information</h3>
            <RadioGroup
              label="Does the insured have a General Liability, Workers' Compensation, OR Business Owners policy?"
              options={['Yes', 'No']}
              value={formData.hasExistingLiabilityPolicy}
              onChange={(val) => updateFormData('hasExistingLiabilityPolicy', val)}
            />
            <RadioGroup
              label="Do all drivers have a valid U.S. driver's license?"
              options={['Yes', 'No']}
              value={formData.allDriversValidLicense}
              onChange={(val) => updateFormData('allDriversValidLicense', val)}
            />
            <RadioGroup
              label="Do any drivers require an SR-22 filing?"
              options={['Yes', 'No']}
              value={formData.anyDriversSR22}
              onChange={(val) => updateFormData('anyDriversSR22', val)}
            />
            <div style={{ marginTop: '16px' }}>
              <label style={{ fontFamily: 'Inter, sans-serif', fontWeight: '500', color: colors.nearBlack, display: 'block', marginBottom: '8px' }}>
                Business Operations (check all that apply)
              </label>
              <CheckboxGroup
                options={businessOperationsCheckOptions}
                values={formData.businessOperationsCheck}
                onToggle={(val) => toggleCheckbox('businessOperationsCheck', val)}
              />
            </div>
          </div>
        </div>
      );
    }

    // Step 2: Policy & History
    if (currentStep === 2) {
      return (
        <div>
          <h2 style={sectionTitleStyle}>Policy & Insurance History</h2>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Policy Details</h3>
            <div style={gridTwo}>
              <Input
                label="Desired Effective Date"
                required
                type="date"
                value={formData.effectiveDate}
                onChange={(e) => updateFormData('effectiveDate', e.target.value)}
                error={errors.effectiveDate}
              />
              <Input
                label="Desired Expiration Date"
                type="date"
                value={formData.expirationDate}
                onChange={(e) => updateFormData('expirationDate', e.target.value)}
              />
            </div>
            <Input
              label="Number of Auto Losses (Last 3 Years)"
              type="number"
              min="0"
              placeholder="0"
              value={formData.autoLossesLast3Years}
              onChange={(e) => updateFormData('autoLossesLast3Years', e.target.value)}
            />
            <div style={{ marginTop: '16px' }}>
              <label style={{ fontFamily: 'Inter, sans-serif', fontWeight: '500', color: colors.nearBlack, display: 'block', marginBottom: '8px' }}>
                Existing Policies with Other Carriers (if any)
              </label>
              <CheckboxGroup
                options={hartfordPolicyOptions}
                values={formData.existingHartfordPolicies}
                onToggle={(val) => toggleCheckbox('existingHartfordPolicies', val)}
              />
            </div>
          </div>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Prior Insurance History</h3>
            <RadioGroup
              label="Do you have prior commercial auto insurance?"
              required
              options={['Yes', 'No']}
              value={formData.hasPriorInsurance}
              onChange={(val) => updateFormData('hasPriorInsurance', val)}
              error={errors.hasPriorInsurance}
            />
            {formData.hasPriorInsurance === 'Yes' && (
              <div style={{ marginTop: '16px', padding: '16px', backgroundColor: colors.offWhite, borderRadius: '8px' }}>
                <Select
                  label="Prior Carrier"
                  required
                  options={priorCarrierOptions}
                  value={formData.priorCarrier}
                  onChange={(e) => updateFormData('priorCarrier', e.target.value)}
                  error={errors.priorCarrier}
                />
                <div style={gridTwo}>
                  <Input
                    label="Policy Number"
                    value={formData.priorPolicyNumber}
                    onChange={(e) => updateFormData('priorPolicyNumber', e.target.value)}
                  />
                  <Select
                    label="BI Limits"
                    options={liabilityLimitOptions}
                    value={formData.priorBILimits}
                    onChange={(e) => updateFormData('priorBILimits', e.target.value)}
                  />
                </div>
                <div style={gridTwo}>
                  <CurrencyInput
                    label="Annual Premium"
                    placeholder="5,000"
                    value={formData.priorPremium}
                    onChange={(e) => updateFormData('priorPremium', e.target.value)}
                  />
                  <Select
                    label="Policy Type"
                    options={priorPolicyTypeOptions}
                    value={formData.priorPolicyType}
                    onChange={(e) => updateFormData('priorPolicyType', e.target.value)}
                  />
                </div>
                <div style={gridTwo}>
                  <Input
                    label="Policy Effective Date"
                    type="date"
                    value={formData.priorEffectiveDate}
                    onChange={(e) => updateFormData('priorEffectiveDate', e.target.value)}
                  />
                  <Input
                    label="Policy Expiration Date"
                    type="date"
                    value={formData.priorExpirationDate}
                    onChange={(e) => updateFormData('priorExpirationDate', e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Claims History (Past 3 Years)</h3>
            <RadioGroup
              label="Do you have any claims history in the past 3 years?"
              required
              options={['Yes', 'No']}
              value={formData.hasClaimHistory}
              onChange={(val) => {
                updateFormData('hasClaimHistory', val);
                if (val === 'Yes' && claims.length === 0) {
                  addClaim();
                }
              }}
              error={errors.hasClaimHistory}
            />
            {formData.hasClaimHistory === 'Yes' && (
              <div style={{ marginTop: '16px' }}>
                {claims.map((claim, index) => (
                  <div key={claim.id} style={{ padding: '16px', backgroundColor: colors.offWhite, borderRadius: '8px', marginBottom: '16px', border: `1px solid ${colors.paleTeal}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h4 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: colors.darkTeal, margin: 0 }}>
                        Claim #{index + 1}
                      </h4>
                      {claims.length > 1 && (
                        <button onClick={() => removeClaim(index)} type="button" style={{ background: 'none', border: 'none', color: '#c53030', cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                          Remove
                        </button>
                      )}
                    </div>
                    <div style={gridTwo}>
                      <Input
                        label="Date of Loss"
                        required
                        type="date"
                        value={claim.dateOfLoss}
                        onChange={(e) => updateClaim(index, 'dateOfLoss', e.target.value)}
                        error={claimErrors[index]?.dateOfLoss}
                      />
                      <CurrencyInput
                        label="Payout Amount"
                        placeholder="10,000"
                        value={claim.payoutAmount}
                        onChange={(e) => updateClaim(index, 'payoutAmount', e.target.value)}
                      />
                    </div>
                    <TextArea
                      label="Description of Loss"
                      required
                      placeholder="Describe what happened..."
                      value={claim.description}
                      onChange={(e) => updateClaim(index, 'description', e.target.value)}
                      error={claimErrors[index]?.description}
                    />
                    <div style={gridThree}>
                      <Input
                        label="Driver of Loss"
                        placeholder="Driver name"
                        value={claim.driverOfLoss}
                        onChange={(e) => updateClaim(index, 'driverOfLoss', e.target.value)}
                      />
                      <Select
                        label="Claim Status"
                        required
                        options={claimStatusOptions}
                        value={claim.claimStatus}
                        onChange={(e) => updateClaim(index, 'claimStatus', e.target.value)}
                        error={claimErrors[index]?.claimStatus}
                      />
                      <RadioGroup
                        label="At Fault?"
                        options={['Yes', 'No']}
                        value={claim.atFault}
                        onChange={(val) => updateClaim(index, 'atFault', val)}
                      />
                    </div>
                  </div>
                ))}
                <button onClick={addClaim} type="button" style={buttonGold}>
                  + Add Another Claim
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Step 3: Vehicles
    if (currentStep === 3) {
      return (
        <div>
          <h2 style={sectionTitleStyle}>Vehicle Information</h2>

          <div style={{ backgroundColor: colors.lightestTeal, padding: '16px 20px', borderRadius: '8px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: colors.darkTeal }}>Total Vehicle Value:</span>
              <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '24px', color: colors.darkTeal, marginLeft: '12px' }}>
                ${totalVehicleValue.toLocaleString()}
              </span>
            </div>
            <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: colors.mediumGray }}>
              {vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''} scheduled
            </span>
          </div>

          {vehicles.map((vehicle, index) => (
            <div key={vehicle.id} style={{ ...cardStyle, backgroundColor: colors.offWhite, border: `1px solid ${colors.paleTeal}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h4 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: colors.darkTeal, margin: 0 }}>
                  Vehicle #{index + 1}
                </h4>
                {vehicles.length > 1 && (
                  <button onClick={() => removeVehicle(index)} type="button" style={{ background: 'none', border: 'none', color: '#c53030', cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                    Remove
                  </button>
                )}
              </div>

              <Input
                label="VIN (17 characters)"
                required
                placeholder="1HGBH41JXMN109186"
                value={vehicle.vin}
                onChange={(e) => updateVehicle(index, 'vin', e.target.value.toUpperCase())}
                error={vehicleErrors[index]?.vin}
              />

              <div style={gridTwo}>
                <Select
                  label="Body Type"
                  required
                  options={bodyTypeOptions}
                  value={vehicle.bodyType}
                  onChange={(e) => updateVehicle(index, 'bodyType', e.target.value)}
                  error={vehicleErrors[index]?.bodyType}
                />
                <Select
                  label="Vehicle Class"
                  required
                  options={vehicleClassOptions}
                  value={vehicle.vehicleClass}
                  onChange={(e) => updateVehicle(index, 'vehicleClass', e.target.value)}
                  error={vehicleErrors[index]?.vehicleClass}
                />
              </div>

              {vehicle.bodyType === 'Pick Up' && (
                <Select
                  label="Hitch Type"
                  options={hitchTypeOptions}
                  value={vehicle.hitchType}
                  onChange={(e) => updateVehicle(index, 'hitchType', e.target.value)}
                />
              )}

              <div style={gridThree}>
                <Input
                  label="Gross Vehicle Weight (GVW)"
                  type="number"
                  placeholder="6000"
                  value={vehicle.gvw}
                  onChange={(e) => updateVehicle(index, 'gvw', e.target.value)}
                />
                <Select
                  label="Driving Radius"
                  required
                  options={drivingRadiusOptions}
                  value={vehicle.drivingRadius}
                  onChange={(e) => updateVehicle(index, 'drivingRadius', e.target.value)}
                  error={vehicleErrors[index]?.drivingRadius}
                />
                <Select
                  label="Vehicle Usage"
                  required
                  options={vehicleUsageOptions}
                  value={vehicle.vehicleUsage}
                  onChange={(e) => updateVehicle(index, 'vehicleUsage', e.target.value)}
                  error={vehicleErrors[index]?.vehicleUsage}
                />
              </div>

              <TextArea
                label="How is this vehicle used in the business?"
                placeholder="Describe how this vehicle is used (minimum 5 words)"
                value={vehicle.vehicleUseDescription}
                onChange={(e) => updateVehicle(index, 'vehicleUseDescription', e.target.value)}
              />

              <h5 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: colors.darkTeal, marginTop: '20px', marginBottom: '12px' }}>
                Garaging Location
              </h5>
              <div style={gridThree}>
                <Input
                  label="Garaging City"
                  required
                  value={vehicle.garagingCity}
                  onChange={(e) => updateVehicle(index, 'garagingCity', e.target.value)}
                  error={vehicleErrors[index]?.garagingCity}
                />
                <Select
                  label="Garaging State"
                  options={stateOptions}
                  value={vehicle.garagingState}
                  onChange={(e) => updateVehicle(index, 'garagingState', e.target.value)}
                />
                <ZipInput
                  label="Garaging ZIP"
                  required
                  value={vehicle.garagingZip}
                  onChange={(e) => updateVehicle(index, 'garagingZip', e.target.value)}
                  error={vehicleErrors[index]?.garagingZip}
                />
              </div>

              <h5 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: colors.darkTeal, marginTop: '20px', marginBottom: '12px' }}>
                Ownership & Value
              </h5>
              <div style={gridTwo}>
                <Select
                  label="Vehicle Registered To"
                  options={vehicleRegisteredToOptions}
                  value={vehicle.vehicleRegisteredTo}
                  onChange={(e) => updateVehicle(index, 'vehicleRegisteredTo', e.target.value)}
                />
                <Select
                  label="Registration State"
                  options={stateOptions}
                  value={vehicle.registrationState}
                  onChange={(e) => updateVehicle(index, 'registrationState', e.target.value)}
                />
              </div>
              <div style={gridThree}>
                <RadioGroup
                  label="Individually Owned?"
                  options={['Yes', 'No']}
                  value={vehicle.individuallyOwned}
                  onChange={(val) => updateVehicle(index, 'individuallyOwned', val)}
                />
                <RadioGroup
                  label="Leased?"
                  options={['Yes', 'No']}
                  value={vehicle.isLeased}
                  onChange={(val) => updateVehicle(index, 'isLeased', val)}
                />
                <RadioGroup
                  label="Financed?"
                  options={['Yes', 'No']}
                  value={vehicle.isFinanced}
                  onChange={(val) => updateVehicle(index, 'isFinanced', val)}
                />
              </div>
              <div style={gridThree}>
                <CurrencyInput
                  label="Cost New"
                  placeholder="35,000"
                  value={vehicle.costNew}
                  onChange={(e) => updateVehicle(index, 'costNew', e.target.value)}
                />
                <CurrencyInput
                  label="Current Value"
                  required
                  placeholder="25,000"
                  value={vehicle.currentValue}
                  onChange={(e) => updateVehicle(index, 'currentValue', e.target.value)}
                  error={vehicleErrors[index]?.currentValue}
                />
                <CurrencyInput
                  label="Customization Value"
                  placeholder="0"
                  value={vehicle.customizationValue}
                  onChange={(e) => updateVehicle(index, 'customizationValue', e.target.value)}
                />
              </div>

              <RadioGroup
                label="Additional Attached Equipment?"
                options={['Yes', 'No']}
                value={vehicle.additionalAttachedEquipment}
                onChange={(val) => updateVehicle(index, 'additionalAttachedEquipment', val)}
              />

              <h5 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: colors.darkTeal, marginTop: '20px', marginBottom: '12px' }}>
                Physical Damage Coverage (this vehicle)
              </h5>
              <RadioGroup
                label="Physical Damage Coverage?"
                options={['Yes', 'No']}
                value={vehicle.hasPhysicalDamage}
                onChange={(val) => updateVehicle(index, 'hasPhysicalDamage', val)}
              />
              {vehicle.hasPhysicalDamage === 'Yes' && (
                <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#fff', borderRadius: '6px' }}>
                  <div style={gridTwo}>
                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Inter, sans-serif', fontSize: '14px', marginBottom: '8px' }}>
                        <input
                          type="checkbox"
                          checked={vehicle.hasCompCoverage}
                          onChange={(e) => updateVehicle(index, 'hasCompCoverage', e.target.checked)}
                        />
                        Comprehensive Coverage
                      </label>
                      {vehicle.hasCompCoverage && (
                        <Select
                          label="Comp Deductible"
                          options={autoDeductibleOptions}
                          value={vehicle.compDeductible}
                          onChange={(e) => updateVehicle(index, 'compDeductible', e.target.value)}
                        />
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Inter, sans-serif', fontSize: '14px', marginBottom: '8px' }}>
                        <input
                          type="checkbox"
                          checked={vehicle.hasCollisionCoverage}
                          onChange={(e) => updateVehicle(index, 'hasCollisionCoverage', e.target.checked)}
                        />
                        Collision Coverage
                      </label>
                      {vehicle.hasCollisionCoverage && (
                        <Select
                          label="Collision Deductible"
                          options={autoDeductibleOptions}
                          value={vehicle.collisionDeductible}
                          onChange={(e) => updateVehicle(index, 'collisionDeductible', e.target.value)}
                        />
                      )}
                    </div>
                  </div>
                  {vehicle.hasCompCoverage && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Inter, sans-serif', fontSize: '14px', marginTop: '12px' }}>
                      <input
                        type="checkbox"
                        checked={vehicle.hasFullGlass}
                        onChange={(e) => updateVehicle(index, 'hasFullGlass', e.target.checked)}
                      />
                      Full Glass Coverage
                    </label>
                  )}
                  <div style={{ display: 'flex', gap: '24px', marginTop: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                      <input
                        type="checkbox"
                        checked={vehicle.hasTowing}
                        onChange={(e) => updateVehicle(index, 'hasTowing', e.target.checked)}
                      />
                      Towing Coverage
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                      <input
                        type="checkbox"
                        checked={vehicle.hasRentalReimbursement}
                        onChange={(e) => updateVehicle(index, 'hasRentalReimbursement', e.target.checked)}
                      />
                      Rental Reimbursement
                    </label>
                  </div>
                </div>
              )}
            </div>
          ))}

          <button onClick={addVehicle} type="button" style={buttonGold}>
            + Add Another Vehicle
          </button>
        </div>
      );
    }

    // Step 4: Drivers
    if (currentStep === 4) {
      return (
        <div>
          <h2 style={sectionTitleStyle}>Driver Information</h2>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: colors.mediumGray, marginBottom: '24px' }}>
            Add all drivers who will operate the commercial vehicles.
          </p>

          {drivers.map((driver, index) => (
            <div key={driver.id} style={{ ...cardStyle, backgroundColor: colors.offWhite, border: `1px solid ${colors.paleTeal}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h4 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: colors.darkTeal, margin: 0 }}>
                  Driver #{index + 1}
                </h4>
                {drivers.length > 1 && (
                  <button onClick={() => removeDriver(index)} type="button" style={{ background: 'none', border: 'none', color: '#c53030', cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                    Remove
                  </button>
                )}
              </div>

              <div style={gridThree}>
                <Input
                  label="First Name"
                  required
                  value={driver.firstName}
                  onChange={(e) => updateDriver(index, 'firstName', e.target.value)}
                  error={driverErrors[index]?.firstName}
                />
                <Input
                  label="Last Name"
                  required
                  value={driver.lastName}
                  onChange={(e) => updateDriver(index, 'lastName', e.target.value)}
                  error={driverErrors[index]?.lastName}
                />
                <Select
                  label="Sex"
                  options={sexOptions}
                  value={driver.sex}
                  onChange={(e) => updateDriver(index, 'sex', e.target.value)}
                />
              </div>

              <div style={gridTwo}>
                <Input
                  label="Date of Birth"
                  required
                  type="date"
                  value={driver.dateOfBirth}
                  onChange={(e) => updateDriver(index, 'dateOfBirth', e.target.value)}
                  error={driverErrors[index]?.dateOfBirth}
                />
                <Select
                  label="Marital Status"
                  options={maritalStatusOptions}
                  value={driver.maritalStatus}
                  onChange={(e) => updateDriver(index, 'maritalStatus', e.target.value)}
                />
              </div>

              <RadioGroup
                label="License Type"
                options={licenseTypeOptions}
                value={driver.licenseType}
                onChange={(val) => updateDriver(index, 'licenseType', val)}
              />

              <div style={gridTwo}>
                <Select
                  label="License State"
                  required
                  options={stateOptions}
                  value={driver.licenseState}
                  onChange={(e) => updateDriver(index, 'licenseState', e.target.value)}
                  error={driverErrors[index]?.licenseState}
                />
                <Input
                  label="License Number"
                  required
                  value={driver.licenseNumber}
                  onChange={(e) => updateDriver(index, 'licenseNumber', e.target.value)}
                  error={driverErrors[index]?.licenseNumber}
                />
              </div>

              <RadioGroup
                label="Does this driver have a Commercial Driver's License (CDL)?"
                options={['Yes', 'No']}
                value={driver.hasCDL}
                onChange={(val) => updateDriver(index, 'hasCDL', val)}
              />
              {driver.hasCDL === 'Yes' && (
                <Input
                  label="Date CDL Received"
                  type="date"
                  value={driver.cdlDateReceived}
                  onChange={(e) => updateDriver(index, 'cdlDateReceived', e.target.value)}
                />
              )}

              <div style={{ marginTop: '16px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                  <input
                    type="checkbox"
                    checked={driver.excludeDriver}
                    onChange={(e) => updateDriver(index, 'excludeDriver', e.target.checked)}
                  />
                  Exclude this driver from coverage
                </label>
              </div>

              <RadioGroup
                label="Does this driver require SR-22 filing?"
                options={['Yes', 'No']}
                value={driver.requiresSR22}
                onChange={(val) => updateDriver(index, 'requiresSR22', val)}
              />
            </div>
          ))}

          <button onClick={addDriver} type="button" style={buttonGold}>
            + Add Another Driver
          </button>
        </div>
      );
    }

    // Step 5: Coverage
    if (currentStep === 5) {
      return (
        <div>
          <h2 style={sectionTitleStyle}>Coverage Options</h2>

          {/* What's Covered Info Box */}
          <div style={{ backgroundColor: colors.lightestTeal, border: `1px solid ${colors.paleTeal}`, borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
            <h3 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600', fontSize: '16px', color: colors.darkTeal, margin: '0 0 12px 0' }}>
              What Does Commercial Auto Insurance Cover?
            </h3>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: colors.darkGray, lineHeight: '1.6' }}>
              <p style={{ margin: '0 0 12px 0' }}><strong>Commercial Auto</strong> insurance protects your business vehicles and drivers against:</p>
              <ul style={{ margin: '0 0 12px 0', paddingLeft: '20px' }}>
                <li><strong>Liability:</strong> Injuries or property damage you cause to others</li>
                <li><strong>Collision:</strong> Damage to your vehicles from accidents</li>
                <li><strong>Comprehensive:</strong> Theft, vandalism, weather damage</li>
                <li><strong>Uninsured Motorist:</strong> Protection when others lack insurance</li>
                <li><strong>Hired & Non-Owned Auto:</strong> Coverage for rented vehicles or employee vehicles used for business</li>
              </ul>
              <p style={{ margin: 0, fontSize: '13px', color: colors.mediumGray }}>Coverage applies to owned, leased, hired, and non-owned vehicles used for business purposes.</p>
            </div>
          </div>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Liability Coverage</h3>
            <Select
              label="Liability Limit (BI/PD)"
              required
              options={liabilityLimitOptions}
              value={formData.liabilityLimit}
              onChange={(e) => updateFormData('liabilityLimit', e.target.value)}
              error={errors.liabilityLimit}
            />
            <Select
              label="Medical Payments"
              required
              options={medPayOptions}
              value={formData.medicalPayments}
              onChange={(e) => updateFormData('medicalPayments', e.target.value)}
              error={errors.medicalPayments}
            />
          </div>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Uninsured/Underinsured Motorist</h3>
            <Select
              label="Uninsured Motorist Bodily Injury (UMBI)"
              required
              options={umLimitOptions}
              value={formData.umbiLimit}
              onChange={(e) => updateFormData('umbiLimit', e.target.value)}
              error={errors.umbiLimit}
            />
            <RadioGroup
              label="Uninsured Motorist Property Damage (UMPD)?"
              options={['Yes', 'No']}
              value={formData.hasUMPD}
              onChange={(val) => updateFormData('hasUMPD', val)}
            />
            <Select
              label="Underinsured Motorist (UIM) Limit"
              options={umLimitOptions}
              value={formData.uimLimit}
              onChange={(e) => updateFormData('uimLimit', e.target.value)}
            />
          </div>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Auto Symbols & Additional Coverages</h3>
            <Select
              label="Covered Auto Symbols"
              options={coveredAutoSymbolOptions}
              value={formData.coveredAutoSymbols}
              onChange={(e) => updateFormData('coveredAutoSymbols', e.target.value)}
            />
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: colors.mediumGray, marginTop: '-8px', marginBottom: '16px' }}>
              Symbol 7 (Specifically Described Autos) is the default. Symbol 1 (Any Auto) may require a copy of contract.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={formData.hasHiredAuto}
                  onChange={(e) => updateFormData('hasHiredAuto', e.target.checked)}
                />
                Hired Auto Coverage (Symbol 8)
              </label>
              {formData.hasHiredAuto && (
                <div style={{ marginLeft: '24px' }}>
                  <CurrencyInput
                    label="Annual Vehicle Rental Expense"
                    placeholder="5,000"
                    value={formData.annualRentalExpense}
                    onChange={(e) => updateFormData('annualRentalExpense', e.target.value)}
                  />
                </div>
              )}

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={formData.hasNonOwnedAuto}
                  onChange={(e) => updateFormData('hasNonOwnedAuto', e.target.checked)}
                />
                Non-Owned Auto Coverage (Symbol 9)
              </label>
              {formData.hasNonOwnedAuto && (
                <div style={{ marginLeft: '24px' }}>
                  <Input
                    label="Total Number of Employees"
                    type="number"
                    min="1"
                    placeholder="10"
                    value={formData.totalEmployees}
                    onChange={(e) => updateFormData('totalEmployees', e.target.value)}
                  />
                </div>
              )}

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={formData.hasDriveOtherCar}
                  onChange={(e) => updateFormData('hasDriveOtherCar', e.target.checked)}
                />
                Drive Other Car Coverage
              </label>
            </div>
          </div>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Optional Endorsements</h3>
            <RadioGroup
              label="Additional Driver Endorsement?"
              options={['Yes', 'No']}
              value={formData.hasAdditionalDriverEndorsement}
              onChange={(val) => updateFormData('hasAdditionalDriverEndorsement', val)}
            />
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: colors.mediumGray, marginTop: '-8px', marginBottom: '16px' }}>
              Drivers only need to be reported at new business and each renewal.
            </p>

            <RadioGroup
              label="Rental Reimbursement (Policy Level)?"
              options={['Yes', 'No']}
              value={formData.hasRentalReimbursementPolicy}
              onChange={(val) => updateFormData('hasRentalReimbursementPolicy', val)}
            />
            {formData.hasRentalReimbursementPolicy === 'Yes' && (
              <Select
                label="Rental Reimbursement Limit"
                options={rentalReimbursementLimitOptions}
                value={formData.rentalReimbursementLimit}
                onChange={(e) => updateFormData('rentalReimbursementLimit', e.target.value)}
              />
            )}

            <RadioGroup
              label="Cargo Coverage?"
              options={['Yes', 'No']}
              value={formData.hasCargoCoverage}
              onChange={(val) => updateFormData('hasCargoCoverage', val)}
            />
            {formData.hasCargoCoverage === 'Yes' && (
              <Select
                label="Cargo Limit / Deductible"
                options={cargoLimitOptions}
                value={formData.cargoLimitDeductible}
                onChange={(e) => updateFormData('cargoLimitDeductible', e.target.value)}
              />
            )}
          </div>
        </div>
      );
    }

    // Step 6: Underwriting & Filings
    if (currentStep === 6) {
      return (
        <div>
          <h2 style={sectionTitleStyle}>Underwriting & Filings</h2>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Filings</h3>
            <Input
              label="DOT Number"
              optional
              placeholder="Enter DOT # if applicable"
              value={formData.dotNumber}
              onChange={(e) => updateFormData('dotNumber', e.target.value)}
            />
            <RadioGroup
              label="State Filing Required?"
              options={['Yes', 'No']}
              value={formData.requireStateFilings}
              onChange={(val) => updateFormData('requireStateFilings', val)}
            />
            {formData.requireStateFilings === 'Yes' && (
              <Input
                label="State Filing Number"
                value={formData.stateFilingNumber}
                onChange={(e) => updateFormData('stateFilingNumber', e.target.value)}
              />
            )}
            <RadioGroup
              label="Federal Filing Required?"
              options={['Yes', 'No']}
              value={formData.requireFederalFilings}
              onChange={(val) => updateFormData('requireFederalFilings', val)}
            />
            {formData.requireFederalFilings === 'Yes' && (
              <div style={{ marginTop: '12px' }}>
                <label style={{ fontFamily: 'Inter, sans-serif', fontWeight: '500', color: colors.nearBlack, display: 'block', marginBottom: '8px' }}>
                  Filing Types
                </label>
                <CheckboxGroup
                  options={filingTypeOptions}
                  values={formData.filingTypes}
                  onToggle={(val) => toggleCheckbox('filingTypes', val)}
                />
              </div>
            )}
          </div>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Vehicle & Driver Questions</h3>
            {renderUnderwritingQuestion('Are all owned autos included in this quote?', 'allOwnedAutosIncluded')}
            {renderUnderwritingQuestion('Are all vehicles on the schedule owned by the applicant?', 'allVehiclesOwnedByApplicant')}
            {renderUnderwritingQuestion('Does the applicant obtain MVR verifications / check driving records before employees drive?', 'obtainMVRVerifications')}
            {renderUnderwritingQuestion('Are all regular drivers listed on this application?', 'allRegularDriversListed')}
            {renderUnderwritingQuestion('Are there any employees under the age of 18 with access to owned vehicles?', 'employeesUnder18Access')}
            {renderUnderwritingQuestion('Are there any family driver exposures?', 'familyDriverExposures')}
          </div>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Business Operations</h3>
            {renderUnderwritingQuestion('Does the applicant have >50% interest in another business?', 'has50PercentInterest')}
            {formData.has50PercentInterest === 'Yes' && (
              <div style={{ marginLeft: '20px', marginBottom: '20px' }}>
                <RadioGroup
                  label="Does that other business own vehicles?"
                  options={['Yes', 'No']}
                  value={formData.otherBusinessOwnsVehicles}
                  onChange={(val) => updateFormData('otherBusinessOwnsVehicles', val)}
                />
              </div>
            )}
            {renderUnderwritingQuestion('Is the applicant a subsidiary of another entity or does the applicant have any subsidiaries?', 'isSubsidiaryOrHasSubsidiaries')}
            {renderUnderwritingQuestion('Are any vehicles leased to others by the applicant?', 'vehiclesLeasedToOthers')}
            {renderUnderwritingQuestion('Is non-business use of company vehicles allowed?', 'nonBusinessUseAllowed')}
            {renderUnderwritingQuestion('Do employees use their own autos in the business?', 'employeesUseOwnAutos')}
          </div>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Safety & Operations</h3>
            {renderUnderwritingQuestion('Does the applicant have an active auto safety program in place?', 'hasAutoSafetyProgram')}
            {renderUnderwritingQuestion('Do all vehicles receive regularly scheduled service and maintenance?', 'regularMaintenanceSchedule')}
            {renderUnderwritingQuestion('Is the annual total mileage driven on any vehicle greater than 40,000 miles?', 'anyVehicleOver40kMiles')}
            {renderUnderwritingQuestion('Does the applicant rent heavy vehicles (>20,000 lbs)?', 'rentHeavyVehicles')}
          </div>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Special Operations</h3>
            {renderUnderwritingQuestion('Does the applicant perform snow plowing for others?', 'performSnowPlowing')}
            {formData.performSnowPlowing === 'Yes' && (
              <div style={{ marginLeft: '20px', marginBottom: '20px', padding: '16px', backgroundColor: colors.offWhite, borderRadius: '8px' }}>
                <RadioGroup
                  label="Use Heavy/Extra Heavy Vehicles for plowing?"
                  options={['Yes', 'No']}
                  value={formData.useHeavyPlowVehicles}
                  onChange={(val) => updateFormData('useHeavyPlowVehicles', val)}
                />
                <Input
                  label="Number of Plow Vehicles"
                  type="number"
                  min="0"
                  value={formData.numberOfPlowVehicles}
                  onChange={(e) => updateFormData('numberOfPlowVehicles', e.target.value)}
                />
                <div style={{ marginTop: '12px' }}>
                  <label style={{ fontFamily: 'Inter, sans-serif', fontWeight: '500', color: colors.nearBlack, display: 'block', marginBottom: '8px' }}>
                    Plow Locations
                  </label>
                  <CheckboxGroup
                    options={plowLocationOptions}
                    values={formData.plowLocations}
                    onToggle={(val) => toggleCheckbox('plowLocations', val)}
                  />
                </div>
              </div>
            )}

            {renderUnderwritingQuestion('Do operations involve transporting hazardous materials (flammables, explosives, chemicals)?', 'transportHazmat')}
            {formData.transportHazmat === 'Yes' && (
              <div style={{ marginLeft: '20px', marginBottom: '20px' }}>
                <label style={{ fontFamily: 'Inter, sans-serif', fontWeight: '500', color: colors.nearBlack, display: 'block', marginBottom: '8px' }}>
                  Hazmat Controls in Place
                </label>
                <CheckboxGroup
                  options={hazmatControlOptions}
                  values={formData.hazmatControls}
                  onToggle={(val) => toggleCheckbox('hazmatControls', val)}
                />
              </div>
            )}

            {renderUnderwritingQuestion('Does the applicant use their own vehicles to transport or deliver goods of others?', 'transportGoodsOfOthers')}
            {renderUnderwritingQuestion('Does the applicant provide 24-hour towing services, roadside assistance, or contract towing (AAA, highway patrol, towing for hire)?', 'provideTowingServices')}
            {renderUnderwritingQuestion('Does the applicant provide rental vehicles, leasing, or loaner cars to customers?', 'provideRentalVehicles')}
            {renderUnderwritingQuestion('Does the insured cross state lines?', 'crossStateLines')}
          </div>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>History</h3>
            {renderUnderwritingQuestion('Has any policy or coverage been declined, cancelled, or non-renewed during the prior 3 years?', 'policyDeclinedCancelledLast3Years')}
            {renderUnderwritingQuestion('Have there been any bankruptcies, tax, or credit liens against the applicant in the past 5 years?', 'bankruptcyLiensLast5Years')}
          </div>
        </div>
      );
    }

    // Step 7: Additional Interests
    if (currentStep === 7) {
      return (
        <div>
          <h2 style={sectionTitleStyle}>Additional Interests</h2>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Loss Payee Information</h3>
            <RadioGroup
              label="Do you have Loss Payee information to add?"
              required
              options={['Yes', 'No']}
              value={formData.hasLossPayee}
              onChange={(val) => {
                updateFormData('hasLossPayee', val);
                if (val === 'Yes' && lossPayees.length === 0) {
                  addLossPayee();
                }
              }}
              error={errors.hasLossPayee}
            />
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: colors.mediumGray, marginTop: '-8px' }}>
              Loss payees are typically banks or finance companies with a financial interest in your vehicles.
            </p>

            {formData.hasLossPayee === 'Yes' && (
              <div style={{ marginTop: '16px' }}>
                {lossPayees.map((payee, index) => (
                  <div key={payee.id} style={{ padding: '16px', backgroundColor: colors.offWhite, borderRadius: '8px', marginBottom: '16px', border: `1px solid ${colors.paleTeal}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h4 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: colors.darkTeal, margin: 0 }}>
                        Loss Payee #{index + 1}
                      </h4>
                      {lossPayees.length > 1 && (
                        <button onClick={() => removeLossPayee(index)} type="button" style={{ background: 'none', border: 'none', color: '#c53030', cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                          Remove
                        </button>
                      )}
                    </div>
                    <Input
                      label="Payee Name"
                      placeholder="Bank or finance company name"
                      value={payee.payeeName}
                      onChange={(e) => updateLossPayee(index, 'payeeName', e.target.value)}
                    />
                    <Input
                      label="Street Address"
                      value={payee.payeeStreet}
                      onChange={(e) => updateLossPayee(index, 'payeeStreet', e.target.value)}
                    />
                    <div style={gridThree}>
                      <Input
                        label="City"
                        value={payee.payeeCity}
                        onChange={(e) => updateLossPayee(index, 'payeeCity', e.target.value)}
                      />
                      <Select
                        label="State"
                        options={stateOptions}
                        value={payee.payeeState}
                        onChange={(e) => updateLossPayee(index, 'payeeState', e.target.value)}
                      />
                      <ZipInput
                        label="ZIP Code"
                        value={payee.payeeZip}
                        onChange={(e) => updateLossPayee(index, 'payeeZip', e.target.value)}
                      />
                    </div>
                    <RadioGroup
                      label="Is this also an Additional Interest?"
                      options={['Yes', 'No']}
                      value={payee.isAdditionalInterest}
                      onChange={(val) => updateLossPayee(index, 'isAdditionalInterest', val)}
                    />
                  </div>
                ))}
                <button onClick={addLossPayee} type="button" style={buttonGold}>
                  + Add Another Loss Payee
                </button>
              </div>
            )}
          </div>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Additional Insured Information</h3>
            <RadioGroup
              label="Do you have Additional Insured information to add?"
              required
              options={['Yes', 'No']}
              value={formData.hasAdditionalInsured}
              onChange={(val) => {
                updateFormData('hasAdditionalInsured', val);
                if (val === 'Yes' && additionalInsureds.length === 0) {
                  addAdditionalInsured();
                }
              }}
              error={errors.hasAdditionalInsured}
            />
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: colors.mediumGray, marginTop: '-8px' }}>
              Additional insureds are third parties who need to be listed on your policy (e.g., landlords, clients requiring certificates).
            </p>

            {formData.hasAdditionalInsured === 'Yes' && (
              <div style={{ marginTop: '16px' }}>
                {additionalInsureds.map((insured, index) => (
                  <div key={insured.id} style={{ padding: '16px', backgroundColor: colors.offWhite, borderRadius: '8px', marginBottom: '16px', border: `1px solid ${colors.paleTeal}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h4 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: colors.darkTeal, margin: 0 }}>
                        Additional Insured #{index + 1}
                      </h4>
                      {additionalInsureds.length > 1 && (
                        <button onClick={() => removeAdditionalInsured(index)} type="button" style={{ background: 'none', border: 'none', color: '#c53030', cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                          Remove
                        </button>
                      )}
                    </div>
                    <Input
                      label="Name"
                      placeholder="Company or person name"
                      value={insured.insuredName}
                      onChange={(e) => updateAdditionalInsured(index, 'insuredName', e.target.value)}
                    />
                    <Input
                      label="Street Address"
                      value={insured.insuredStreet}
                      onChange={(e) => updateAdditionalInsured(index, 'insuredStreet', e.target.value)}
                    />
                    <div style={gridThree}>
                      <Input
                        label="City"
                        value={insured.insuredCity}
                        onChange={(e) => updateAdditionalInsured(index, 'insuredCity', e.target.value)}
                      />
                      <Select
                        label="State"
                        options={stateOptions}
                        value={insured.insuredState}
                        onChange={(e) => updateAdditionalInsured(index, 'insuredState', e.target.value)}
                      />
                      <ZipInput
                        label="ZIP Code"
                        value={insured.insuredZip}
                        onChange={(e) => updateAdditionalInsured(index, 'insuredZip', e.target.value)}
                      />
                    </div>
                    <RadioGroup
                      label="Waiver of Subrogation?"
                      options={['Yes', 'No']}
                      value={insured.waiverOfSubrogation}
                      onChange={(val) => updateAdditionalInsured(index, 'waiverOfSubrogation', val)}
                    />
                  </div>
                ))}
                <button onClick={addAdditionalInsured} type="button" style={buttonGold}>
                  + Add Another Additional Insured
                </button>
              </div>
            )}
          </div>

          <div style={cardStyle}>
            <h3 style={subheadingStyle}>Additional Notes</h3>
            <TextArea
              label="Anything else we should know?"
              placeholder="Any additional information about your vehicles, drivers, or operations..."
              value={formData.additionalNotes}
              onChange={(e) => updateFormData('additionalNotes', e.target.value)}
            />
          </div>
        </div>
      );
    }

    // Step 8: Review
    if (submitStatus === 'success') {
      return (
        <SuccessPage
          title="Quote Request Submitted Successfully!"
          message="Thank you for your submission. A ContractorNerd agent will review your information and contact you with quote options."
          email="charlie@contractornerd.com"
          additionalInfo={`Total Vehicle Value Submitted: $${totalVehicleValue.toLocaleString()}`}
        />
      );
    }

    const editButtonStyle = {
      background: 'none',
      border: 'none',
      color: colors.teal,
      fontFamily: 'Inter, sans-serif',
      fontSize: '13px',
      cursor: 'pointer',
      textDecoration: 'underline',
      padding: '0',
      marginLeft: '12px',
    };

    const sectionHeaderStyle = {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
    };

    return (
      <div>
        <h2 style={sectionTitleStyle}>Review Your Information</h2>
        {submitStatus === 'error' && (() => {
          // Collect actual error messages to display
          const errorMessages = Object.entries(errors).filter(([, msg]) => msg).map(([field, message]) => ({ key: field, message }));

          return (
            <div style={{ backgroundColor: '#FED7D7', border: '1px solid #C53030', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
              <h3 style={{ color: '#C53030', margin: '0 0 8px 0', fontFamily: 'Poppins, sans-serif' }}>Please Fix Errors</h3>
              {errorMessages.length > 0 ? (
                <>
                  <p style={{ color: '#C53030', margin: '0 0 12px 0', fontFamily: 'Inter, sans-serif' }}>The following fields need attention:</p>
                  <ul style={{ color: '#C53030', margin: 0, paddingLeft: '20px', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                    {errorMessages.map(({ key, message }) => (
                      <li key={key}>{message}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <p style={{ color: '#C53030', margin: 0, fontFamily: 'Inter, sans-serif' }}>
                  There was a problem submitting your form. Please try again or contact us at charlie@contractornerd.com
                </p>
              )}
            </div>
          );
        })()}

        <div style={{ backgroundColor: colors.paleGold, padding: '20px', borderRadius: '8px', marginBottom: '24px', border: `1px solid ${colors.gold}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px', color: colors.nearBlack, margin: 0 }}>
            Please review your information below, then submit your quote request.
          </p>
          <button onClick={handleSubmit} type="button" disabled={isSubmitting} style={isSubmitting ? buttonDisabled : buttonPrimary}>
            {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
          </button>
        </div>

        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <h3 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: colors.darkTeal, margin: 0 }}>Company Information</h3>
            <button type="button" onClick={() => goToStep(1)} style={editButtonStyle}>Edit</button>
          </div>
          <div style={gridTwo}>
            <ReviewItem label="Business Name" value={formData.legalBusinessName} />
            <ReviewItem label="Entity Type" value={formData.legalEntity} />
            {formData.hasDba === 'Yes' && <ReviewItem label="DBA" value={formData.dbaName} />}
            <ReviewItem label="Contact" value={`${formData.contactFirstName} ${formData.contactLastName}`} />
            <ReviewItem label="Email" value={formData.contactEmail} />
            <ReviewItem label="Phone" value={formData.contactPhone} />
            <ReviewItem label="Business Address" value={`${formData.businessStreet}, ${formData.businessCity}, ${formData.businessState} ${formData.businessZip}`} />
          </div>
        </div>

        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <h3 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: colors.darkTeal, margin: 0 }}>Policy & History</h3>
            <button type="button" onClick={() => goToStep(2)} style={editButtonStyle}>Edit</button>
          </div>
          <div style={gridTwo}>
            <ReviewItem label="Effective Date" value={formatDate(formData.effectiveDate)} />
            <ReviewItem label="Prior Insurance" value={formData.hasPriorInsurance} />
            {formData.hasPriorInsurance === 'Yes' && <ReviewItem label="Prior Carrier" value={formData.priorCarrier} />}
            <ReviewItem label="Claims History" value={formData.hasClaimHistory === 'Yes' ? `Yes (${claims.length} claim${claims.length !== 1 ? 's' : ''})` : 'No'} />
          </div>
        </div>

        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <h3 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: colors.darkTeal, margin: 0 }}>Vehicles ({vehicles.length})</h3>
            <button type="button" onClick={() => goToStep(3)} style={editButtonStyle}>Edit</button>
          </div>
          <div style={{ backgroundColor: colors.lightestTeal, padding: '12px 16px', borderRadius: '6px', marginBottom: '16px', display: 'inline-block' }}>
            <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: colors.darkTeal }}>
              Total Value: ${totalVehicleValue.toLocaleString()}
            </span>
          </div>
          {vehicles.map((vehicle, idx) => (
            <div key={vehicle.id} style={{ padding: '12px', backgroundColor: colors.offWhite, borderRadius: '6px', marginBottom: '8px', fontFamily: 'Inter, sans-serif', fontSize: '14px', color: colors.darkGray }}>
              <strong>VIN: {vehicle.vin}</strong> - {vehicle.bodyType}
              <br />
              <span style={{ fontSize: '13px', color: colors.mediumGray }}>
                {vehicle.vehicleClass} | {vehicle.vehicleUsage} | Garaging: {vehicle.garagingCity}, {vehicle.garagingState} {vehicle.garagingZip}
              </span>
            </div>
          ))}
        </div>

        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <h3 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: colors.darkTeal, margin: 0 }}>Drivers ({drivers.length})</h3>
            <button type="button" onClick={() => goToStep(4)} style={editButtonStyle}>Edit</button>
          </div>
          {drivers.map((driver) => (
            <div key={driver.id} style={{ padding: '12px', backgroundColor: colors.offWhite, borderRadius: '6px', marginBottom: '8px', fontFamily: 'Inter, sans-serif', fontSize: '14px', color: colors.darkGray }}>
              <strong>{driver.firstName} {driver.lastName}</strong>
              {driver.excludeDriver && <span style={{ color: '#C53030', marginLeft: '8px' }}>(Excluded)</span>}
              <br />
              <span style={{ fontSize: '13px', color: colors.mediumGray }}>
                License: {driver.licenseState} {driver.licenseNumber}
                {driver.hasCDL === 'Yes' && ' | CDL'}
              </span>
            </div>
          ))}
        </div>

        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <h3 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: colors.darkTeal, margin: 0 }}>Coverage</h3>
            <button type="button" onClick={() => goToStep(5)} style={editButtonStyle}>Edit</button>
          </div>
          <div style={gridTwo}>
            <ReviewItem label="Liability Limit" value={formData.liabilityLimit} />
            <ReviewItem label="Medical Payments" value={formData.medicalPayments} />
            <ReviewItem label="UMBI" value={formData.umbiLimit} />
            <ReviewItem label="UIM" value={formData.uimLimit || 'Not selected'} />
            <ReviewItem label="Hired Auto" value={formData.hasHiredAuto ? 'Yes' : 'No'} />
            <ReviewItem label="Non-Owned Auto" value={formData.hasNonOwnedAuto ? 'Yes' : 'No'} />
          </div>
        </div>

        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <h3 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: colors.darkTeal, margin: 0 }}>Additional Interests</h3>
            <button type="button" onClick={() => goToStep(7)} style={editButtonStyle}>Edit</button>
          </div>
          <div style={gridTwo}>
            <ReviewItem label="Loss Payees" value={formData.hasLossPayee === 'Yes' ? `Yes (${lossPayees.length})` : 'No'} />
            <ReviewItem label="Additional Insureds" value={formData.hasAdditionalInsured === 'Yes' ? `Yes (${additionalInsureds.length})` : 'No'} />
          </div>
        </div>

        <div style={{ backgroundColor: colors.paleGold, padding: '20px', borderRadius: '8px', marginTop: '24px', border: `1px solid ${colors.gold}` }}>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: colors.nearBlack, margin: 0 }}>
            By submitting this form, you confirm that the information provided is accurate to the best of your knowledge.
            A ContractorNerd agent will review your submission and contact you with quote options.
          </p>
        </div>
      </div>
    );
  };

  return (
    <FormWrapper
      title="Commercial Auto Insurance Quote"
      subtitle="Get your commercial vehicles covered. Takes about 10-15 minutes."
    >
      <StepIndicator steps={STEPS} currentStep={currentStep} onStepClick={goToStep} />
      <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />

      {renderStepContent()}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '32px', paddingTop: '24px', borderTop: `1px solid ${colors.lighterGray}` }}>
        {currentStep > 1 ? (
          <button onClick={prevStep} type="button" style={buttonSecondary}>← Previous</button>
        ) : (
          <div />
        )}
        {currentStep < totalSteps ? (
          <button onClick={nextStep} type="button" style={buttonPrimary}>Continue →</button>
        ) : submitStatus === 'success' ? (
          <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: colors.darkTeal }}>Submitted Successfully</div>
        ) : (
          <button onClick={handleSubmit} type="button" disabled={isSubmitting} style={isSubmitting ? buttonDisabled : buttonPrimary}>
            {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
          </button>
        )}
      </div>
    </FormWrapper>
  );
}
